/**
 * A public ticketed event card: the catalog's Events tab and the top of the
 * event page (Event Tickets plan 2026-10-19, U4, R9, R10).
 *
 * Prices are the all-in prices the server computed (fees inside, R10).
 */

'use client';

import React from 'react';
import Link from 'next/link';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { proxyStorageUrl } from '@/utils/storage/proxyStorageUrl';
import type { PublicTicketedEvent } from '@/utils/eventTickets/publicTicketTypes';
import { ticketEventWhen, ticketPriceRange, ticketVenueLine } from '@/utils/eventTickets/ticketEventDisplay';
import { PUBLIC_EVENT_COPY, salesStateLabel } from './publicEventCopy';

export function EventFacts({ event }: { event: PublicTicketedEvent }) {
  const venue = ticketVenueLine(event);
  const price = ticketPriceRange(event.min_all_in_cents, event.max_all_in_cents);
  return (
    <ul className="space-y-2 text-sm text-gray-700">
      <li className="flex items-start gap-2">
        <GoogleMaterialIcon name="schedule" size="sm" className="mt-0.5 text-gray-500" />
        <span>{ticketEventWhen(event)}</span>
      </li>
      {venue ? (
        <li className="flex items-start gap-2">
          <GoogleMaterialIcon name="location_on" size="sm" className="mt-0.5 text-gray-500" />
          <span>{venue}</span>
        </li>
      ) : null}
      {price ? (
        <li className="flex items-start gap-2">
          <GoogleMaterialIcon name="confirmation_number" size="sm" className="mt-0.5 text-gray-500" />
          <span>
            {price}
            {event.fees_included ? <span className="text-gray-500"> · {PUBLIC_EVENT_COPY.FEES_INCLUDED}</span> : null}
          </span>
        </li>
      ) : null}
      {event.outside_venue && event.host_location_name ? (
        <li className="flex items-start gap-2 text-gray-500">
          <GoogleMaterialIcon name="storefront" size="sm" className="mt-0.5" />
          <span>Hosted by {event.host_location_name}</span>
        </li>
      ) : null}
    </ul>
  );
}

export function EventCover({ event, className = '' }: { event: PublicTicketedEvent; className?: string }) {
  const cover = proxyStorageUrl(event.cover_image_url);
  if (!cover) {
    return (
      <div className={`flex items-end p-4 ${className}`} style={{ background: 'var(--store-primary)' }}>
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--store-primary-text)' }}>
          Event
        </span>
      </div>
    );
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={cover} alt={event.cover_image_alt || ''} className={`object-cover ${className}`} />;
}

export default function PublicEventCard({ event, href }: { event: PublicTicketedEvent; href: string }) {
  const state = salesStateLabel(event.sales_state);
  return (
    <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="public-event-card">
      <EventCover event={event} className="h-40 w-full" />
      <div className="space-y-3 p-5">
        <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
        {event.description ? <p className="line-clamp-2 text-sm text-gray-600">{event.description}</p> : null}
        <EventFacts event={event} />
        <Link
          href={href}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full px-5 text-sm font-semibold"
          style={{ backgroundColor: 'var(--store-primary)', color: 'var(--store-primary-text)' }}
        >
          {/* Every public catalog card says View Details (owner rule, ui-patterns.md);
              the buying happens on the event's own page. */}
          {PUBLIC_EVENT_COPY.VIEW_DETAILS}
        </Link>
        {state ? <p className="text-center text-sm text-gray-500">{state}</p> : null}
      </div>
    </article>
  );
}
